import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Chip } from 'primereact/chip';
import {
  AnalysisListResponse,
  AnalysisMethod
} from '@/api/domains/analyses/types';
import { getStatusSeverity, formatTimeAgo } from '../../utils';
import { LoadingSkeletons } from '../LoadingSkeletons/LoadingSkeletons';
import styles from './RecentActivitySection.module.css';

interface RecentActivitySectionProps {
  recentAnalyses: AnalysisListResponse | undefined;
  isLoading: boolean;
  error: any;
}

const getMethodDisplayName = (method: AnalysisMethod | undefined): string => {
  if (!method) return '';

  const methodNames: Record<AnalysisMethod, string> = {
    VECTOR_ONLY: 'Quick Scan',
    HYBRID: 'Smart Analysis',
    RAG_ENHANCED: 'Deep Dive'
  };

  return methodNames[method] || method;
};

export const RecentActivitySection = ({
  recentAnalyses,
  isLoading,
  error
}: RecentActivitySectionProps) => {
  const navigate = useNavigate();

  return (
    <section className={styles.recentActivitySection}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>
          <i className="pi pi-chart-line" style={{ marginRight: '10px' }}></i>
          Recent Analyses
        </h2>
        <Button
          label="View All"
          icon="pi pi-arrow-right"
          iconPos="right"
          outlined
          className={styles.viewAllButton}
          onClick={() => navigate('/analyses')}
        />
      </div>
      <div className={styles.activityList}>
        {isLoading ? (
          <LoadingSkeletons />
        ) : error ? (
          <Card className={styles.errorCard}>
            <div className={styles.errorContent}>
              <i
                className="pi pi-exclamation-triangle"
                style={{
                  fontSize: '2rem',
                  color: '#ff6b6b'
                }}
              ></i>
              <p>Error loading analyses: {error.message}</p>
            </div>
          </Card>
        ) : (
          recentAnalyses?.analyses.map((analysis) => (
            <Card
              key={analysis.analysis_id}
              className={styles.activityCard}
              onClick={() => navigate(`/analyses/${analysis.analysis_id}`)}
              style={{ cursor: 'pointer' }}
            >
              <div className={styles.activityHeader}>
                <div className={styles.activityTitle}>
                  <i
                    className="pi pi-chart-line"
                    style={{
                      marginRight: '8px',
                      color: '#377dff'
                    }}
                  ></i>
                  {analysis.analysis_name}
                </div>
                <Tag
                  value={analysis.status}
                  severity={getStatusSeverity(analysis.status)}
                  className={styles.activityStatus}
                />
              </div>
              <div className={styles.activityDetails}>
                <div className={styles.activityTime}>
                  <i className="pi pi-clock" style={{ marginRight: '6px' }}></i>
                  {formatTimeAgo(analysis.created_at)}
                </div>
                {analysis.method && (
                  <div className={styles.activityMethod}>
                    <i className="pi pi-cog" style={{ marginRight: '6px' }}></i>
                    {getMethodDisplayName(analysis.method)}
                  </div>
                )}
                <div className={styles.activityFindings}>
                  <Chip
                    label={`${analysis.findings_count} findings`}
                    className={styles.findingsChip}
                  />
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </section>
  );
};
